import { FC, useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { TPartnersRow, TReferralsPartnersRow } from 'redux/reducers/referrals/types';
import { useDispatch, useSelector } from 'react-redux';
import {
	getPartners,
	getPartnersIsLoad,
	getReferralsPartners,
	getReferralsPartnersIsLoad,
} from 'redux/reducers/referrals/selectors';
import { getPartnersRequest } from 'redux/reducers/referrals/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { IRequestWithPageSortFilterSearch, TFilters, TOrderBy } from 'redux/types';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { TTableStructure } from 'ui/EasTable/types';
import { tableStructure } from './tableStructure';
import { ReferralsTabs } from '../Referrals/ReferralsTabs';
import { AddPartnerDialog } from './AddPartnerDialog';
import { PartnerReportsDialog } from './PartnerReportsDialog';

export const ReferralsPartners: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const partners = useSelector(getPartners);
	const partnersIsLoading = useSelector(getPartnersIsLoad);
	const [openPartnerDialog, setOpenPartnerDialog] = useState(false);
	const [openEditPartnerDialog, setOpenEditPartnerDialog] = useState(false);
	const [partnerToEdit, setPartnerToEdit] = useState<TPartnersRow | null>(null);
	const [partnerForReports, setPartnerForReports] = useState<TPartnersRow | null>(null);
	const [partnersResponse, setPartnersResponse] = useState<IRequestWithPageSortFilterSearch>({
		pageNumber: 1,
		pageSize: PAGE_SIZE,
		searchString: '',
	});
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);
	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector] && item.type === 'date') {
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			// console.log('dates', dates);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		const orderBy = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? 'desc'
						: 'asc'
					: 'desc',
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: 'desc',
		} as TOrderBy);
		dispatch(
			getPartnersRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TPartnersRow>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const handleEditPartner = useCallback((partner: TPartnersRow) => {
		setOpenEditPartnerDialog(true);
		setPartnerToEdit(partner);
	}, []);

	const handleAction = useCallback(
		(id: number, typeOfDialog: string) => {
			if (!partners?.data) return;
			const partner = partners.data.find((p: TPartnersRow) => p.id === id);
			if (!partner) return;
			if (typeOfDialog === 'Edit') {
				handleEditPartner(partner);
			}
			if (typeOfDialog === 'Reports') {
				setPartnerForReports(partner);
			}
		},
		[handleEditPartner, partners],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<ReferralsTabs />
			<EasTableWithFilter<TPartnersRow>
				tableStructure={currentTableStructure}
				items={partners}
				itemsIsLoading={partnersIsLoading}
				handleAction={handleAction}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Typography variant="body1" color="primary">
						Extra Services / Referral Hub / <b>Partners</b>
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => setOpenPartnerDialog(true)}
					>
						Add Partner
					</Button>
				</Box>
			</EasTableWithFilter>
			<AddPartnerDialog
				open={openPartnerDialog}
				onClose={() => setOpenPartnerDialog(false)}
				title="Add Partner"
				updatePageRequestData={partnersResponse}
				partnerToEdit={null}
			/>
			<AddPartnerDialog
				open={openEditPartnerDialog}
				onClose={() => setOpenEditPartnerDialog(false)}
				title="Edit Partner"
				updatePageRequestData={partnersResponse}
				partnerToEdit={partnerToEdit}
			/>
			<PartnerReportsDialog
				open={!!partnerForReports}
				onClose={() => setPartnerForReports(null)}
				partnerId={partnerForReports?.id ?? null}
				partnerName={partnerForReports?.partnerName ?? ''}
			/>
		</Box>
	);
};
